import { useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  HexIcon,
  Icons,
  Line,
  Placeholder,
  Stack,
  TimeXAxis,
  Typography,
  YAxis,
} from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { formatCurrency } from '@cast/utils';
import { TopMostExpensiveGroup } from '@cast/split-me/types';
import { attachTimeTicks } from '@cast/split-me/utils';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { ChartWithTopWorkloadsCard } from '../../../_components/ui/ChartWithTopWorkloadsCard';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { ActionRequired } from './_components/ActionRequired';
import { useGpuIdleSpendData } from './_hooks/useGpuIdleSpendData';
import { useGpuInventoryDateRange } from './_hooks/useGpuInventoryDateRange';
import { GpuIdleSpendChartDatum } from './_types';

type GpuIdleSpendChartProps = {
  chartData: GpuIdleSpendChartDatum[];
  topItems: TopMostExpensiveGroup[];
  isLoading: boolean;
  timeZone: string;
};

const GpuIdleSpendChart = ({
  chartData,
  topItems,
  isLoading,
  timeZone,
}: GpuIdleSpendChartProps) => (
  <Stack flex={1} minHeight={200}>
    <ChartContainer<GpuIdleSpendChartDatum>
      data={chartData}
      isLoading={isLoading}
      sx={{ width: '100%', height: '100%' }}
      margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
      testId="gpu-idle-spend-chart"
    >
      <ChartGrid hideVertical />
      {topItems.map((item) => (
        <Line<GpuIdleSpendChartDatum>
          key={item.id}
          dataKey={(d) => d.values[item.id] ?? 0}
          stroke={item.color}
          strokeWidth={2}
          dot={false}
          tooltipConfig={{ displayName: item.name }}
        />
      ))}
      <YAxis
        tickCount={5}
        tickFormatter={(value: string | number) => `$${value}`}
        padding={{ top: 30 }}
        allowDecimals
      />
      <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
      <ChartTooltip<GpuIdleSpendChartDatum>
        testId="data-popper"
        header={(headerProps) => (
          <ChartTooltipTimestampHeader {...headerProps} timezone={timeZone} />
        )}
        formatValue={(value) =>
          formatCurrency(Number(value), { maximumFractionDigits: 2 })
        }
      />
    </ChartContainer>
  </Stack>
);

const TITLE = 'Idle GPU spend';

export const GpuIdleSpendCard = () => {
  const { timeZone } = useGpuInventoryDateRange();
  const {
    topItems,
    datapoints,
    isLoading,
    isFetching,
    isCapabilitiesLoading,
    hasGpuMetrics,
    isDataProcessing,
    isGpuExporterInstalled,
    error,
    refetch,
  } = useGpuIdleSpendData();
  const { getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<string>();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const chartData = useMemo<GpuIdleSpendChartDatum[]>(
    () =>
      attachTimeTicks(datapoints || []).map((entry) => ({
        ...entry,
        values: Object.entries(entry.values).reduce(
          (acc, [key, value]) => ({ ...acc, [key]: value ?? 0 }),
          {}
        ),
      })),
    [datapoints]
  );

  const listItems = useMemo(
    () =>
      topItems.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        value: formatCurrency(item.cost, { maximumFractionDigits: 2 }),
      })),
    [topItems]
  );

  const visibleTopItems = useMemo(
    () => topItems.filter((item) => !isMetricDisabled(item.id)),
    [topItems, isMetricDisabled]
  );

  const cardContent = (
    <ChartWithTopWorkloadsCard
      testId="gpu-idle-spend-card"
      leftHeader={<Typography variant="A1">{TITLE}</Typography>}
      rightHeader={<Typography variant="A1">Highest to Lowest</Typography>}
      chart={
        <GpuIdleSpendChart
          chartData={chartData}
          topItems={visibleTopItems}
          isLoading={isCardLoading}
          timeZone={timeZone}
        />
      }
      items={listItems}
      isListLoading={isCardLoading && !topItems.length}
      onItemClick={toggleMetric}
      isItemDisabled={isMetricDisabled}
      getItemHref={(item) =>
        getWorkloadDashboardPath(item.id, WORKLOAD_CATEGORIES.GPU_UTILIZATION)
      }
    />
  );

  if (hasError && !isCardLoading) {
    return (
      <WidgetLayout testId="gpu-idle-spend-card" padding={false}>
        <FailedToLoad title="Failed to load idle GPU spend" refresh={refetch} />
      </WidgetLayout>
    );
  }

  if (!isCapabilitiesLoading && !isGpuExporterInstalled && !hasGpuMetrics) {
    return (
      <WidgetLayout testId="gpu-idle-spend-card" padding={false}>
        <ActionRequired />
      </WidgetLayout>
    );
  }

  if (!isCapabilitiesLoading && isDataProcessing) {
    return (
      <WidgetLayout testId="gpu-idle-spend-card" padding={false}>
        <Placeholder
          title="Data is being processed"
          body="For newly connected clusters, it might take up to 15 minutes for the data to be available."
          icon={
            <HexIcon
              variant="notification"
              icon={<Icons.HourglassHigh weight="fill" />}
              size={84}
            />
          }
        />
      </WidgetLayout>
    );
  }

  return cardContent;
};
